import { useEffect, useState } from 'react';
import { History, X, ExternalLink, Newspaper, List as ListIcon } from 'lucide-react';
import { draftsApi, ApiError } from '../lib/api';
import type { RecentCoverageDraft } from '@shared/types';

interface Props {
  excludeDraftId: string;
  onClose: () => void;
}

const DAYS = 8;

export default function RecentCoverageModal({ excludeDraftId, onClose }: Props) {
  const [drafts, setDrafts] = useState<RecentCoverageDraft[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    draftsApi
      .recentCoverage({ days: DAYS, excludeDraftId })
      .then((res) => { if (!cancelled) setDrafts(res.drafts); })
      .catch((e) => { if (!cancelled) setErr(e instanceof ApiError ? e.message : 'Failed to load coverage'); });
    return () => { cancelled = true; };
  }, [excludeDraftId]);

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
          <div className="w-8 h-8 rounded-md bg-amber-50 border border-amber-100 flex items-center justify-center">
            <History size={14} className="text-amber-700" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-slate-900 text-sm">Recent coverage</div>
            <div className="text-xs text-slate-500">Story headlines and bulletin items from the last {DAYS} days · so you don't double-cover</div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded text-slate-500"><X size={16} /></button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {err ? (
            <p className="p-6 text-sm text-red-600">{err}</p>
          ) : drafts === null ? (
            <p className="p-6 text-sm text-slate-400">Loading…</p>
          ) : drafts.length === 0 ? (
            <p className="p-8 text-sm text-slate-500 text-center">Nothing covered in the last {DAYS} days.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {drafts.map((d) => (
                <DraftSection key={d.id} draft={d} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function DraftSection({ draft }: { draft: RecentCoverageDraft }) {
  const dateLabel = new Date(draft.anchor_at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const anchorHint =
    draft.anchor_kind === 'sent' ? 'sent' :
    draft.anchor_kind === 'scheduled' ? 'scheduled' :
    'created';

  return (
    <li className="px-5 py-4">
      <div className="flex items-baseline gap-2 mb-2">
        <span className="text-sm font-semibold text-slate-900">{dateLabel}</span>
        <span className="text-xs text-slate-500 truncate flex-1">— {draft.name}</span>
        <StatusPill status={draft.status} />
        <span className="text-[10px] uppercase tracking-wide text-slate-400">{anchorHint}</span>
        <a
          href={`/draft/${draft.id}`}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-blue-700 hover:text-blue-900 flex items-center gap-1"
          title="Open this draft in a new tab"
        >
          open <ExternalLink size={11} />
        </a>
      </div>
      <ul className="space-y-1 ml-1">
        {draft.headlines.map((h, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
            {h.kind === 'story' ? (
              <Newspaper size={13} className="mt-0.5 text-slate-400 flex-shrink-0" />
            ) : (
              <ListIcon size={13} className="mt-0.5 text-slate-400 flex-shrink-0" />
            )}
            <span>{h.headline}</span>
          </li>
        ))}
      </ul>
    </li>
  );
}

function StatusPill({ status }: { status: RecentCoverageDraft['status'] }) {
  const cls =
    status === 'sent' ? 'bg-emerald-100 text-emerald-700' :
    status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
    'bg-slate-100 text-slate-600';
  return (
    <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded font-semibold ${cls}`}>
      {status}
    </span>
  );
}
